import {
  ClinicChainSearchResponseDto,
  ClinicOwnerCreationDto,
  ClinicOwnerResponseDto,
  ClinicOwnerStaffListResponseDto,
  ClinicOwnerUpdateDto,
  CommonEntitiesListResponse,
  ConfirmSignUpDto,
  PaymentDto,
  ClinicOwnerPermissionDto,
  ClinicOwnerPermissionResponseDto,
  ApplyPromocodeDto,
  ApplyPromocodeResponseDto,
  InvoiceResponseDto,
  ClinicOwnerSubscriptionDetailsResponseDto,
} from '@docbay/schemas';
import axiosInstance from 'integration/api/axiosInstance';
import axiosAuthInstance from './axiosAuthInstance';
import {
  fetchClinicOwnerStaffProps,
  searchClinicsInChainProps,
} from 'common/types/clinicOwner';
import { getLanguageFromLocalStorage } from 'applicaiton/sessionStorage/language';

/**
 * An API service for managing clinic owners.
 */
export const ClinicOwnerAPI = {
  /**
   * Signs up a new clinic owner.
   *
   * @param data - The data to use for creating the clinic owner.
   * @returns A promise that resolves with the created clinic owner.
   */
  clinicOwnerSignUp: (data: ClinicOwnerCreationDto) => {
    const language = getLanguageFromLocalStorage();
    return axiosAuthInstance('').post<ClinicOwnerResponseDto>(
      '/clinic-owner/sign-up',
      {
        ...data,
        language,
      },
    );
  },

  /**
   * Confirms the sign up of a clinic owner.
   *
   * @param data - The data to use for confirming the sign up.
   * @returns A promise that resolves with the confirmed clinic owner.
   */
  clinicOwnerConfirmSignUp: (data: ConfirmSignUpDto) =>
    axiosAuthInstance('').post('/clinic-owner/confirm-sign-up', data),

  /**
   * Gets the current clinic owner.
   *
   * @returns A promise that resolves with the current clinic owner.
   */
  getClinicOwner: () => axiosInstance.get('/clinic-owner'),

  /**
   * Makes a payment for the clinic owner.
   *
   * @param data - The data to use for making the payment.
   * @param token - The authentication token.
   * @returns A promise that resolves with the invoice response.
   */
  clinicOwnerPayments: (data: PaymentDto, token: string) =>
    axiosAuthInstance(token).post<InvoiceResponseDto>(
      '/clinic-owner/payment',
      data,
    ),

  /**
   * Gets a clinic owner by ID.
   *
   * @param id - The ID of the clinic owner to retrieve.
   * @returns A promise that resolves with the retrieved clinic owner.
   */
  getClinicOwnerById: (id: string) =>
    axiosInstance.get(
      `/clinic-owner/${id}?include=clinics,photo,user,professional`,
    ),

  /**
   * Updates a clinic owner by ID.
   *
   * @param id - The ID of the clinic owner to update.
   * @param data - The data to use for updating the clinic owner.
   * @returns A promise that resolves with the updated clinic owner.
   */
  updateClinicOwner: (id: string, data: ClinicOwnerUpdateDto) =>
    axiosInstance.patch(`/clinic-owner/${id}`, data),

  /**
   * Deletes a clinic owner by ID.
   *
   * @param id - The ID of the clinic owner to delete.
   * @returns A promise that resolves with the deleted clinic owner.
   */
  deleteClinicOwner: (id: string) =>
    axiosInstance.delete(`/clinic-owner/${id}`),

  /**
   * Gets a list of clinics by owner ID.
   *
   * @param clincId - The ID of the clinic to retrieve.
   * @returns A promise that resolves with the retrieved clinics.
   */
  getClinicsByIdOwner: (clincId: string) =>
    axiosInstance.get(`/clinic-owner/${clincId}/clinics`),

  /**
   * Resends confirmation code for a given email.
   *
   * @param email - The email to send the confirmation code to.
   * @returns A promise that resolves with the confirmation code response.
   */
  resendConfirmationCode: (email: string) => {
    const language = getLanguageFromLocalStorage();
    return axiosAuthInstance('').post(
      '/clinic-owner/resend-confirmation-code',
      {
        email,
        language,
      },
    );
  },

  /**
   * Searches clinics in a chain.
   *
   * @param props - The search properties to use for searching the clinics.
   * @returns A promise that resolves with the search response.
   */
  searchClinicsInChain: ({
    entityType,
    entityId,
    searchItem,
    page,
    limit,
  }: searchClinicsInChainProps) => {
    const params = {
      includeDeleted: 'false',
      include: 'owner,address',
      searchEntityType: entityType,
      searchEntityId: entityId,
      ...(page ? { page } : {}),
      ...(limit ? { limit } : {}),
      ...(searchItem ? { search: searchItem } : {}),
    };
    return axiosInstance.get<
      CommonEntitiesListResponse<ClinicChainSearchResponseDto>
    >('/clinic-owner/search-clinics-in-chain', {
      params,
    });
  },

  /**
   * Gets a list of permissions for a clinic owner by ID.
   *
   * @param id - The ID of the clinic owner to retrieve permissions for.
   * @returns A promise that resolves with the retrieved permissions.
   */
  getPermissionsByClinicOwnerId: (id: string) =>
    axiosInstance.get<ClinicOwnerPermissionResponseDto[]>(
      `/clinic-owner/${id}/permissions`,
    ),

  /**
   * Updates a list of permissions for a clinic owner by ID.
   *
   * @param id - The ID of the clinic owner to update permissions for.
   * @param data - The data to use for updating the permissions.
   * @returns A promise that resolves with the updated permissions.
   */
  updatePermissionsByClinicOwnerId: (
    id: string,
    data: ClinicOwnerPermissionDto,
  ) => axiosInstance.put(`/clinic-owner/${id}/permissions`, data),

  /**
   * Fetches a list of clinic owner staff.
   *
   * @param props - The fetch properties to use for fetching the staff.
   * @returns A promise that resolves with the fetched staff.
   */
  fetchClinicOwnerStaff: ({
    page,
    limit,
    clinicIds,
    userTypes,
  }: fetchClinicOwnerStaffProps) => {
    const params = {
      includeDeleted: 'false',
      include: 'photo',
      ...(userTypes?.length ? { userTypes } : {}),
      ...(page ? { page } : {}),
      ...(limit ? { limit } : {}),
      ...(clinicIds?.length ? { clinicIds } : {}),
    };

    return axiosInstance.get<ClinicOwnerStaffListResponseDto>(
      '/clinic-owner/staff',
      {
        params,
      },
    );
  },

  /**
   * Applies a promotional code for a given clinic owner.
   *
   * @param data - The data to use for applying the promotional code.
   * @param token - The authentication token.
   * @returns A promise that resolves with the applied promotional code response.
   */
  applyClinicOwnerPromocode: (data: ApplyPromocodeDto, token: string) =>
    axiosAuthInstance(token).post<ApplyPromocodeResponseDto>(
      '/clinic-owner/apply-promocode',
      data,
    ),

  /**
   * Gets a list of subscriptions for the clinic owner.
   *
   * @returns A promise that resolves with the retrieved subscriptions.
   */
  getSubscriptions: () => {
    return axiosInstance.get<ClinicOwnerSubscriptionDetailsResponseDto>(
      'clinic-owner/subscription',
    );
  },

  /**
   * Gets a link to the customer portal for the clinic owner.
   *
   * @returns A promise that resolves with the retrieved customer portal link.
   */
  getCustomerPortalLink: () => {
    return axiosInstance.get('clinic-owner/billing');
  },

  /**
   * Gets a link to the customer billing page for the clinic owner by ID.
   *
   * @param id - The ID of the clinic owner to retrieve the billing page link for.
   * @returns A promise that resolves with the retrieved customer billing page link.
   */
  getCustomerBillingLink: (id: string) => {
    return axiosInstance.get(`clinic-owner/${id}/payment/account-status`);
  },

  /**
   * Gets a link to update the customer billing account for the clinic owner.
   *
   * @returns A promise that resolves with the retrieved link.
   */
  getCustomerBillingUpdateLink: () => {
    return axiosInstance.get('clinic-owner/payment/account-update-link');
  },

  /**
   * Gets a link to activate the customer billing account for the clinic owner.
   *
   * @returns A promise that resolves with the retrieved link.
   */
  getCustomerBillingActivationLink: () => {
    return axiosInstance.get('clinic-owner/payment/account-activation-link');
  },

  /**
   * Gets a photo package for the clinic owner.
   *
   * @param token - The authentication token.
   * @returns A promise that resolves with the retrieved photo package.
   */
  getPhotoPackage: (token: string) => {
    return axiosAuthInstance(token).get('clinic-owner/request-photo-package');
  },

  /**
   * Gets the account status for a given clinic owner by ID.
   *
   * @param id - The ID of the clinic owner to retrieve the account status for.
   * @returns A promise that resolves with the retrieved account status.
   */
  getAccountStatus: (id: string) => {
    return axiosInstance.get(`/clinic-owner/${id}/payment/account-status`);
  },

  /**
   * Deletes an account for a given clinic owner by ID.
   *
   * @param id - The ID of the clinic owner to delete the account for.
   * @returns A promise that resolves with the deleted account.
   */
  deleteCOAccount: (id: string) => {
    return axiosInstance.delete(`/clinic-owner/${id}`);
  },

  /**
   * Generates a client secret for SEPA payments for the clinic owner.
   *
   * @param token - The authentication token.
   * @returns A promise that resolves with the generated client secret.
   */
  getClientSecret: (token: string) => {
    return axiosAuthInstance(token).post(
      '/clinic-owner/generate-sepa-client-secret',
    );
  },
};
